import { Button, ChakraProps, Spinner } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { Token } from '@app/types/token';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useAddress, useSigner } from '@thirdweb-dev/react';
import { useGetRevealWindowForBlindAuction } from '@app/hooks/nft/useGetRevealWindowForBlindAuction';
import { useHasActiveBids } from '@app/hooks/nft/useHasActiveBids';
import { useSettleListing } from '@app/hooks/nft/useSettleListing';
import { MdOutlineDownloadDone } from 'react-icons/md';
import { useIsBidder } from '@app/hooks/nft/useIsBidder';

type Props = {
  listing: NFTMarketplaceListing;
  sellCurrency: Token | undefined;
  variant?: 'rowCompact' | 'secondary' | 'primary';
} & ChakraProps;

export const SettleListingButton: FC<Props> = ({
  sellCurrency,
  listing,
  variant,
  ...rest
}) => {
  const address = useAddress();
  const { data: revealWindow } = useGetRevealWindowForBlindAuction();
  const { data: hasActiveBid } = useHasActiveBids(listing.id);
  const { data: isBidder } = useIsBidder(listing.id);
  const { mutate: settleListing, isLoading: isSettling } = useSettleListing();
  const signer = useSigner();

  const isAvailable = useMemo(() => {
    if (!listing || !listing.isNFTAvailable) {
      return false;
    }

    const isMaker = address?.toLowerCase() === listing.maker?.toLowerCase();

    if (!isMaker && !isBidder) {
      return false;
    }

    const now = Date.now();

    switch (listing.type) {
      case 'ENGLISH_AUCTION': {
        if (now < listing.end * 1000) {
          return false;
        }

        return true;
      }
      case 'BLIND': {
        if (now < listing.end * 1000 + revealWindow) {
          return false;
        }

        return true;
      }
      default: {
        return false;
      }
    }
  }, [listing, address, revealWindow, isBidder]);

  if (!isAvailable) {
    return null;
  }

  return (
    <Button
      width="100%"
      variant={variant}
      leftIcon={
        isSettling ? (
          <Spinner width={3} height={3} />
        ) : (
          <MdOutlineDownloadDone size={16} />
        )
      }
      isDisabled={isSettling}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();

        settleListing({
          listingId: listing.id,
          signer
        });
      }}
      {...rest}
    >
      {isSettling ? 'Processing' : 'Settle'}
    </Button>
  );
};
